import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsApi } from '@/api/endpoints';
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  AlertCircle
} from 'lucide-react';
import { TestRun } from '@/types';

export const TestRunDetailsPage = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [testRun, setTestRun] = useState<TestRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRunDetails();
     
  }, [runId]);

  const fetchRunDetails = async () => {
    if (!runId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await testsApi.getRunDetails(runId);
      const runData = response.data.data;
      // Backend returns { run: {...}, results: [...] }
      // Merge results into the run object to match our interface
      const runWithResults = {
        ...runData.run,
        results: runData.results
      };
      setTestRun(runWithResults);
    } catch (err: any) {
      console.error('Error fetching run details:', err);
      setError(err?.response?.data?.message || 'Failed to load run details');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (milliseconds?: number) => {
    if (!milliseconds) return 'N/A';
    const seconds = milliseconds / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'failed':
        return 'bg-red-500/20 text-red-400';
      case 'running':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-white/10 text-white/70';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-white/50">Loading test run details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/tests')}
            className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:shadow-glow-teal transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!testRun) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/tests')}
            className="flex items-center space-x-2 px-4 py-2 bg-white/5 border border-white/[0.06] text-white/70 rounded-lg hover:bg-white/10 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Test Run Details</h1>
            <p className="text-white/50 mt-1">Run ID: {testRun.id}</p>
          </div>
        </div>
      </div>

      {/* Run Summary */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Status</p>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(testRun.status)}`}>
              {testRun.status.charAt(0).toUpperCase() + testRun.status.slice(1)}
            </span>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Total Tests</p>
            <p className="text-2xl font-bold text-white">{testRun.total_tests}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Passed</p>
            <p className="text-2xl font-bold text-emerald-400">{testRun.passed_tests}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Failed</p>
            <p className="text-2xl font-bold text-red-400">{testRun.failed_tests}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Skipped</p>
            <p className="text-2xl font-bold text-white/50">{testRun.skipped_tests}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Duration</p>
            <p className="text-2xl font-bold text-white">{formatDuration(testRun.duration_ms)}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Started At</p>
            <p className="text-sm font-medium text-white">{formatTimestamp(testRun.started_at)}</p>
          </div>
          {testRun.completed_at && (
            <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
              <p className="text-sm text-white/50 mb-1">Completed At</p>
              <p className="text-sm font-medium text-white">{formatTimestamp(testRun.completed_at)}</p>
            </div>
          )}
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Environment</p>
            <p className="text-sm font-medium text-white">{testRun.environment}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/[0.06]">
            <p className="text-sm text-white/50 mb-1">Triggered By</p>
            <p className="text-sm font-medium text-white">{testRun.triggered_by}</p>
          </div>
        </div>
      </div>

      {/* Test Results */}
      {testRun.results && testRun.results.length > 0 && (
        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Test Results ({testRun.results.length})</h2>
          <div className="space-y-3">
            {testRun.results.map((result, index) => (
              <div key={index} className="bg-white/5 border border-white/[0.06] rounded-lg p-4 hover:bg-white/[0.08] transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {result.status === 'passed' ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-white">{result.test_name}</p>
                        {result.test_description && (
                          <p className="text-sm text-white/50 mt-1">{result.test_description}</p>
                        )}
                      </div>
                    </div>

                    {/* HTTP Details */}
                    {result.http_method && result.endpoint && (
                      <div className="mt-2 bg-teal-500/10 border border-teal-500/20 rounded p-3">
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="font-semibold text-teal-400">{result.http_method}</span>
                          <span className="text-teal-300">{result.endpoint}</span>
                          {result.response_status && (
                            <span className={`ml-auto px-2 py-1 rounded text-xs font-semibold ${
                              result.response_status >= 200 && result.response_status < 300
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {result.response_status}
                            </span>
                          )}
                        </div>
                        {result.response_time_ms && (
                          <div className="mt-1 text-xs text-teal-400/70">
                            Response time: {result.response_time_ms}ms
                          </div>
                        )}
                      </div>
                    )}

                    {/* Error Message */}
                    {result.error_message && (
                      <div className="mt-2 bg-red-500/10 border border-red-500/20 rounded p-3">
                        <p className="text-sm font-semibold text-red-400 mb-1">Error:</p>
                        <p className="text-sm text-red-400/80 font-mono whitespace-pre-wrap">
                          {result.error_message}
                        </p>
                      </div>
                    )}

                    {/* Assertions */}
                    {result.assertions_total > 0 && (
                      <div className="mt-2 text-sm text-white/50">
                        Assertions: {result.assertions_passed}/{result.assertions_total} passed
                      </div>
                    )}
                  </div>

                  <div className="ml-4 text-right flex-shrink-0">
                    <span className="text-sm text-white/50 flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatDuration(result.duration_ms)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {(!testRun.results || testRun.results.length === 0) && (
        <div className="glass-card p-6">
          <p className="text-center text-white/50">No test results available</p>
        </div>
      )}
    </div>
  );
};

export default TestRunDetailsPage;
